/**
 * AWARE Health Frontend — Forecast Trend Chart (v3 — theme-aware)
 *
 * PRIMARY DISPLAY RULE: Use `forecast_calibrated_median`.
 * DO NOT render forecast_mean or forecast_median.
 *
 * v3 changes:
 *  - Theme-aware: light/dark Plotly layout colors
 *  - Translated annotations
 */

import Plot from 'react-plotly.js'
import type { Data, Annotations } from 'plotly.js'
import type { ForecastRecord } from '../../types/forecast'
import { useTheme } from '../../contexts/ThemeContext'

interface ForecastTrendChartProps {
  records: ForecastRecord[]
  regionName: string
  country: string
}

const TEAL   = '#14b8a6'
const AMBER  = '#f59e0b'

export default function ForecastTrendChart({
  records,
  regionName,
  country,
}: ForecastTrendChartProps) {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  if (records.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: isDark ? '#334155' : '#94a3b8',
          fontSize: 14,
        }}
      >
        No forecast data available for this region.
      </div>
    )
  }

  const sorted = [...records].sort((a, b) => a.horizon - b.horizon)
  const xDates = sorted.map((r) => r.target_start)

  const threshLow  = sorted[0]?.threshold_low  ?? null
  const threshHigh = sorted[0]?.threshold_high ?? null

  const yMax = Math.max(
    ...sorted.map((r) => r.pi95_high),
    threshHigh ?? 0,
  ) * 1.15

  // ── Risk zone background shapes ─────────────────────────────────────
  const shapes: any[] = []

  if (threshLow !== null && threshHigh !== null) {
    shapes.push({
      type: 'rect', xref: 'paper', yref: 'y',
      x0: 0, x1: 1, y0: 0, y1: threshLow,
      fillcolor: isDark ? 'rgba(16,185,129,0.06)' : 'rgba(16,185,129,0.08)',
      line: { width: 0 }, layer: 'below',
    })
    shapes.push({
      type: 'rect', xref: 'paper', yref: 'y',
      x0: 0, x1: 1, y0: threshLow, y1: threshHigh,
      fillcolor: isDark ? 'rgba(245,158,11,0.07)' : 'rgba(245,158,11,0.08)',
      line: { width: 0 }, layer: 'below',
    })
    shapes.push({
      type: 'rect', xref: 'paper', yref: 'y',
      x0: 0, x1: 1, y0: threshHigh, y1: yMax,
      fillcolor: isDark ? 'rgba(239,68,68,0.07)' : 'rgba(239,68,68,0.07)',
      line: { width: 0 }, layer: 'below',
    })
    shapes.push({
      type: 'line', xref: 'paper', yref: 'y',
      x0: 0, x1: 1, y0: threshLow, y1: threshLow,
      line: { color: 'rgba(16,185,129,0.5)', width: 1, dash: 'dot' },
    })
    shapes.push({
      type: 'line', xref: 'paper', yref: 'y',
      x0: 0, x1: 1, y0: threshHigh, y1: threshHigh,
      line: { color: 'rgba(239,68,68,0.5)', width: 1, dash: 'dot' },
    })
  }

  // ── Annotations ────────────────────────────────────────────────────
  const annotations: Partial<Annotations>[] = []

  if (threshLow !== null) {
    annotations.push({
      xref: 'paper', yref: 'y',
      x: 1.01, y: threshLow,
      xanchor: 'left',
      text: `Low<br>${threshLow.toFixed(3)}`,
      showarrow: false,
      font: { size: 9, color: isDark ? 'rgba(16,185,129,0.7)' : 'rgba(16,185,129,0.9)', family: 'JetBrains Mono, monospace' },
    })
  }
  if (threshHigh !== null) {
    annotations.push({
      xref: 'paper', yref: 'y',
      x: 1.01, y: threshHigh,
      xanchor: 'left',
      text: `High<br>${threshHigh.toFixed(3)}`,
      showarrow: false,
      font: { size: 9, color: isDark ? 'rgba(239,68,68,0.7)' : 'rgba(239,68,68,0.9)', family: 'JetBrains Mono, monospace' },
    })
  }

  annotations.push({
    xref: 'paper', yref: 'paper',
    x: 0, y: -0.14,
    xanchor: 'left',
    text: '⚕ Primary: forecast_calibrated_median (PI50 midpoint, bias-corrected) · Shaded bands: PI50 & PI95 · Risk zones: model thresholds',
    showarrow: false,
    font: { size: 9, color: isDark ? '#334155' : '#94a3b8', family: 'Inter, sans-serif' },
  })

  // ── Traces ────────────────────────────────────────────────────────
  const markerBorderColor = isDark ? '#050a14' : '#ffffff'

  const pi95Upper: Data = {
    x: xDates, y: sorted.map((r) => r.pi95_high),
    type: 'scatter', mode: 'lines',
    name: '95% PI upper', line: { color: 'transparent' },
    showlegend: false, hoverinfo: 'skip' as const,
  }
  const pi95Lower: Data = {
    x: xDates, y: sorted.map((r) => r.pi95_low),
    type: 'scatter', mode: 'lines',
    name: '95% Prediction Interval',
    fill: 'tonexty',
    fillcolor: isDark ? 'rgba(20,184,166,0.09)' : 'rgba(13,148,136,0.08)',
    line: { color: 'transparent' },
    hoverinfo: 'skip' as const,
  }

  const pi50Upper: Data = {
    x: xDates, y: sorted.map((r) => r.pi50_high),
    type: 'scatter', mode: 'lines',
    name: '50% PI upper', line: { color: 'transparent' },
    showlegend: false, hoverinfo: 'skip' as const,
  }
  const pi50Lower: Data = {
    x: xDates, y: sorted.map((r) => r.pi50_low),
    type: 'scatter', mode: 'lines',
    name: '50% Prediction Interval',
    fill: 'tonexty',
    fillcolor: isDark ? 'rgba(20,184,166,0.20)' : 'rgba(13,148,136,0.15)',
    line: { color: 'transparent' },
    hoverinfo: 'skip' as const,
  }

  const calibratedMedian: Data = {
    x: xDates,
    y: sorted.map((r) => r.forecast_calibrated_median),
    type: 'scatter', mode: 'lines+markers',
    name: 'Calibrated Median',
    line: { color: TEAL, width: 3 },
    marker: {
      color: TEAL, size: 9,
      line: { color: markerBorderColor, width: 2 },
    },
    hovertemplate:
      '<b>%{x}</b><br>' +
      `<span style="color:${TEAL}">Calibrated Median: %{y:.4f}</span>` +
      '<extra></extra>',
  }

  const hasActuals = sorted.some((r) => r.actual_rate !== null)
 const actualTrace: Data | null = hasActuals
    ? {
        x: xDates,
        y: sorted.map((r) => r.actual_rate),
        type: 'scatter' as const, // <-- Add 'as const' here
        mode: 'lines+markers',
        name: 'Actual Rate (test)',
        line: { color: AMBER, width: 2, dash: 'dash' },
        marker: { color: AMBER, size: 7, symbol: 'diamond', line: { color: markerBorderColor, width: 1.5 } },
        hovertemplate:
          '<b>%{x}</b><br>' +
          `<span style="color:${AMBER}">Actual: %{y:.4f}</span>` +
          '<extra></extra>',
      }
    : null

  const traces: Data[] = [
    pi95Upper, pi95Lower, pi50Upper, pi50Lower,
    calibratedMedian,
    ...(actualTrace ? [actualTrace] : []),
  ]

  const layout: any = {
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: isDark ? 'rgba(10,18,32,0.5)' : 'rgba(248,250,252,0.6)',
    font: {
      color: isDark ? '#94a3b8' : '#64748b',
      family: 'Inter, sans-serif',
      size: 12,
    },

    title: {
      text: `<b style="color:${isDark ? '#f1f5f9' : '#0f172a'}">${regionName}</b><span style="color:${isDark ? '#475569' : '#94a3b8'}"> · ${country}</span>`,
      font: { size: 15, family: 'Inter, sans-serif', color: isDark ? '#f1f5f9' : '#0f172a' },
      x: 0.01, xanchor: 'left',
      pad: { t: 4 },
    },

    xaxis: {
      title: { text: 'Target Period', font: { color: isDark ? '#475569' : '#94a3b8', size: 11 } },
      gridcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)',
      linecolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)',
      tickfont: { color: isDark ? '#64748b' : '#94a3b8', size: 11 },
      tickangle: -20,
    },
    yaxis: {
      title: { text: 'Incidence Rate (per 100k)', font: { color: isDark ? '#475569' : '#94a3b8', size: 11 } },
      gridcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)',
      linecolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)',
      tickfont: { color: isDark ? '#64748b' : '#94a3b8', size: 11, family: 'JetBrains Mono, monospace' },
      rangemode: 'tozero',
      range: [0, yMax],
    },

    legend: {
      bgcolor: isDark ? 'rgba(5,10,20,0.85)' : 'rgba(255,255,255,0.9)',
      bordercolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
      borderwidth: 1,
      font: { color: isDark ? '#94a3b8' : '#64748b', size: 11 },
      orientation: 'h',
      x: 0, y: -0.1, xanchor: 'left',
    },

    hovermode: 'x unified',
    hoverlabel: {
      bgcolor: isDark ? '#080e1c' : '#ffffff',
      bordercolor: '#14b8a6',
      font: { color: isDark ? '#e2e8f0' : '#1e293b', size: 12 },
    },

    margin: { t: 48, r: 80, b: 90, l: 64 },
    shapes,
    annotations,
  }

  return (
    <Plot
      data={traces}
      layout={layout}
      config={{
        displayModeBar: true,
        modeBarButtonsToRemove: [
          'select2d', 'lasso2d', 'autoScale2d', 'hoverClosestCartesian',
          'hoverCompareCartesian', 'toggleSpikelines',
        ],
        displaylogo: false,
        responsive: true,
        toImageButtonOptions: {
          format: 'png',
          filename: `AWARE_${country}_${regionName}_forecast`,
          scale: 2,
        },
      }}
      style={{ width: '100%', height: '100%' }}
      useResizeHandler
    />
  )
}